// Builds the de-duplicated "Shared with" people list from a SharePoint GetSharingInformation
// `permissionsInformation` block.
//
// A recipient can hold access through EITHER of two independent mechanisms, and a complete list needs both:
//   1. A direct permission grant            -> permissionsInformation.principals[]      (entry shape: { principal, role })
//   2. Membership of a sharing LINK          -> permissionsInformation.links[].linkMembers[]   (flat principal objects)
//      (the modern default — "Specific people" / organization links — where access is granted via the link,
//       so the recipient never appears in `principals`). Not-yet-redeemed invitees are also surfaced via
//      links[].linkDetails.Invitations[].invitee.
//
// Reading `principals` alone silently drops every link-shared guest. This merges both, normalizes link
// members/invitees to the same `{ principal }` shape as direct grants, and de-duplicates so a recipient on
// several links — or on both a direct grant and a link — is listed once.

// A SharePoint collection is a plain array under minimal metadata, or wrapped as { results: [...] } under verbose.
const asArray = (value: any): any[] =>
  Array.isArray(value) ? value : (Array.isArray(value?.results) ? value.results : []);

// Stable identity for de-duplication: loginName is the most reliable, with email/name as fallbacks.
const principalKey = (entry: any): string => {
  const p = entry?.principal ?? {};
  return (p.loginName ?? p.LoginName ?? p.email ?? p.Email ?? p.name ?? p.Name ?? "").toString().toLowerCase().trim();
};

export const mergeSharedWithPrincipals = (permissions: any): any[] => {
  // Direct grants already wrap the real principal under `.principal`; keep them as-is.
  const directPrincipals = asArray(permissions?.principals ?? permissions?.Principals);

  // Link shares expose members (and not-yet-redeemed invitees) as FLAT principal objects; wrap each as
  // { principal } so it matches the direct-grant shape consumers render (`entry.principal.name`).
  const linkPrincipals = asArray(permissions?.links ?? permissions?.Links).flatMap((link: any) => {
    const linkDetails = link?.linkDetails ?? link?.LinkDetails ?? {};
    const members = asArray(link?.linkMembers ?? link?.LinkMembers);
    const invitees = asArray(linkDetails?.Invitations ?? linkDetails?.invitations)
      .map((invitation: any) => invitation?.invitee ?? invitation?.Invitee)
      .filter(Boolean);
    return [...members, ...invitees].map((member: any) => ({ principal: member }));
  });

  const seen = new Set<string>();
  const merged: any[] = [];
  for (const entry of [...directPrincipals, ...linkPrincipals]) {
    if (!entry?.principal) continue;
    const key = principalKey(entry);
    if (key && seen.has(key)) continue;
    if (key) seen.add(key);
    merged.push(entry);
  }
  return merged;
};

// SharePoint principalType for an individual user (vs 8 = SharePoint group, 4 = security group, etc.).
const PRINCIPAL_TYPE_USER = 1;

// A non-empty trimmed string that looks like an email (contains "@"), or undefined.
const asAddress = (value: any): string | undefined => {
  const s = (value ?? "").toString().trim();
  return s.includes("@") ? s : undefined;
};

// Turns a UPN into a display address. A B2B guest UPN like
// "adelev_docubird.onmicrosoft.com#EXT#@xblox.onmicrosoft.com" is reconstructed to the guest's real email
// "adelev@docubird.onmicrosoft.com" — the segment before #EXT#, with the encoded "_" (the original "@")
// turned back into "@". The encoded "@" is the LAST "_" because mail domains never contain underscores.
// A normal UPN (no #EXT#) is returned as-is when it already looks like an email.
const addressFromUpn = (value: any): string | undefined => {
  const upn = (value ?? "").toString().trim();
  if (!upn) return undefined;
  const extIndex = upn.toLowerCase().indexOf("#ext#");
  if (extIndex === -1) return upn.includes("@") ? upn : undefined;

  const encoded = upn.substring(0, extIndex);            // e.g. "adelev_docubird.onmicrosoft.com"
  const lastUnderscore = encoded.lastIndexOf("_");
  const reconstructed = lastUnderscore === -1
    ? encoded
    : `${encoded.substring(0, lastUnderscore)}@${encoded.substring(lastUnderscore + 1)}`;
  return reconstructed.includes("@") ? reconstructed : undefined;
};

/**
 * Resolves a display email address for a "shared with" principal, trying the cleanest source first:
 *   1. `email`               — already the friendly address (set for link members and most users).
 *   2. `userPrincipalName`   — reconstructed when it is a #EXT# guest UPN, else used as-is.
 *   3. `loginName`           — the claims form `i:0#.f|membership|<upn>`; the trailing UPN is parsed as in (2).
 * Returns undefined when nothing usable is found (e.g. a group, which has no address).
 */
export const resolvePrincipalEmail = (principal: any): string | undefined => {
  const fromEmail = asAddress(principal?.email ?? principal?.Email);
  if (fromEmail) return fromEmail;

  const fromUpn = addressFromUpn(principal?.userPrincipalName ?? principal?.UserPrincipalName);
  if (fromUpn) return fromUpn;

  const login = (principal?.loginName ?? principal?.LoginName ?? "").toString();
  const trailingUpn = login.includes("|") ? login.substring(login.lastIndexOf("|") + 1) : login;
  return addressFromUpn(trailingUpn);
};

/**
 * Builds the "shared with" row label. Individual USERS get their address appended in brackets
 * ("Adele Vance (adelev@docubird.onmicrosoft.com)") so every user is unambiguously identifiable even when
 * SharePoint only stored their alias as the name. Groups (and any non-user principal) show their name only.
 * The brackets are omitted when there is no address, or when the name already IS the address.
 */
export const formatSharedWithPrincipalLabel = (principal: any): string => {
  const name = (principal?.name ?? principal?.Name ?? principal?.email ?? principal?.Email ?? "").toString().trim();

  if (principal?.principalType !== PRINCIPAL_TYPE_USER) return name;   // groups etc. -> name only

  const email = resolvePrincipalEmail(principal);
  if (!email) return name;
  if (!name) return email;
  if (name.toLowerCase() === email.toLowerCase()) return name;        // avoid "x@y.com (x@y.com)"
  return `${name} (${email})`;
};
